import { useContext, useEffect, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { FormProvider, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { useQueryClient } from "@tanstack/react-query";
import * as yup from "yup";

import MyButton from "../../../components/MyButton";
import MyTextField from "../../../components/inputs/MyTextField";
import MyEditor from "../../../components/inputs/MyEditor";
import { useBoardItem } from "../../../hooks/useBoardItem";
import { SiteContext } from "../../../context/SiteProvider";
import MyInputFile from "../../../components/inputs/MyInputFile";
import MyCheckboxCircle from "../../../components/inputs/MyCheckboxCircle";
import Privacy from "../../Privacy";
import MyInputCaptcha from "../../../components/inputs/MyInputCaptcha";
import { useFile } from "../../../hooks/useFile";
import { AuthContext } from "../../../context/AuthProvider";
import { doUploadFiles, doUploadSingleFile } from "../../../utils/file";
import { renderBody } from "../../../utils";
import {
  BOARD_PERMISSION,
  NON_MEMBER_PERMISSIONS,
} from "../../../constants/permission";
import {
  CheckRoleAndPermissionDisplay,
  checkRoleAndPermissions,
  getPermissionMessage,
} from "../../../components/CheckRoleAndPermission";

const FamilyUpsert06 = ({ boardCode }) => {
  const navigate = useNavigate();
  const { itemNo } = useParams();
  const {
    createBoardItemMutation,
    updateBoardItemMutation,
    useGetBoardItem,
    useGetBoard,
  } = useBoardItem();
  const queryClient = useQueryClient();
  const { setLoadingScreen } = useContext(SiteContext);
  const { getFileUploadUrl } = useFile();
  const { isAuthenticated, profile, roleCode, isAdmin } =
    useContext(AuthContext);

  const privateViewToken = new URLSearchParams(window.location.search).get(
    "privateViewToken"
  );
  const anonymousEditToken = new URLSearchParams(window.location.search).get(
    "anonymousEditToken"
  );

  const nonPermissions = NON_MEMBER_PERMISSIONS[boardCode];

  const { data: board, isLoading: isBoardLoading } = useGetBoard(
    { boardCode },
    (data) => data.data
  );

  const { data, isLoading } = useGetBoardItem(
    {
      boardCode: board?.boardCode,
      itemNo,
      privateViewToken,
      enabled:
        checkRoleAndPermissions(
          roleCode,
          board,
          BOARD_PERMISSION.READ,
          nonPermissions,
          isAdmin
        ) &&
        checkRoleAndPermissions(
          roleCode,
          board,
          BOARD_PERMISSION.WRITE,
          nonPermissions,
          isAdmin
        ),
    },
    (data) => data.data
  );

  const initValues = {
    email: "",
    title: "",
    writerName: "",
    password: "",
    isPrivate: false,
    privacyAccept: false,
    body: "",
    link: "",
    files: [],
    captchaToken: "",
    isAuthenticated: false,
    videoUrl: "",
    isRequiredCaptcha: false,
    embeddedFiles: [],
  };

  const validationSchema = yup.object({
    email: yup
      .string()
      .email("이메일 형식이 올바르지 않습니다.")
      .required("필수 입력 항목입니다."),
    title: yup.string().required("필수 입력 항목입니다."),
    captcha: yup.string().when("isRequiredCaptcha", {
      is: true,
      then: () =>
        yup.string().required("자동입력방지 인증코드를 입력해주세요."),
    }),
    writerName: yup.string().when("isAuthenticated", {
      is: false,
      then: () => yup.string().required("필수 입력 항목입니다."),
      otherwise: () => yup.string().notRequired(),
    }),
    writerPassword: yup.string().when("isAuthenticated", {
      is: false,
      then: () => yup.string().required("필수 입력 항목입니다."),
      otherwise: () => yup.string().notRequired(),
    }),
    body: yup.string().required("필수 입력 항목입니다."),
    privacyAccept: yup
      .boolean()
      .oneOf([true], "미동의 시 게시글을 작성할 수 없습니다.")
      .required(),
    files: yup
      .array()
      .of(yup.mixed().required("파일을 선택해 주세요."))
      .max(5, "최대 5개의 파일만 업로드할 수 있습니다.")
      .nullable(),
  });

  const methods = useForm({
    defaultValues: initValues,
    resolver: yupResolver(validationSchema),
  });

  const showCaptcha = useMemo(
    () => !!board?.options?.useCaptchaInPost,
    [board]
  );

  const checkShowSubmitButton = useMemo(() => {
    let result = !itemNo;

    if (!!itemNo) {
      if (isAuthenticated) {
        result = isAdmin;

        if (!isAdmin && data?.writer?.userId) {
          result = profile?.userId === data?.writer?.userId;
        }
      } else {
        result = !data?.writer?.userId;
      }
    }

    return result;
  }, [data?.writer?.userId, isAdmin, isAuthenticated, itemNo, profile?.userId]);

  const onSubmit = async (vals) => {
    if (
      checkRoleAndPermissions(
        roleCode,
        board,
        BOARD_PERMISSION.WRITE,
        nonPermissions,
        isAdmin
      )
    ) {
      const payload = {
        title: vals.title,
        body: vals.body,
        isPrivate: vals.isPrivate,
        additionalItems: {
          videoUrl: vals.videoUrl,
          privacyAccept: vals.privacyAccept,
        },
        writerEmail: vals.email,
      };

      if (showCaptcha) {
        if (!vals.captchaToken) {
          methods.setError("captcha", {
            message: "인증을 진행해주세요.",
          });
          return;
        }
        payload.captchaToken = vals.captchaToken;
      }

      setLoadingScreen(true);

      let uploadedFiles = [];
      const files = [];

      if (vals.files?.length) {
        vals.files.forEach((file) => {
          files.push(file);
        });
      }

      if (files.length) {
        uploadedFiles = await doUploadFiles(files, getFileUploadUrl, {
          boardCode: board?.boardCode,
        });
      }

      payload.attachedFiles = uploadedFiles.map((file) => ({
        fileName: file.name,
        fileId: file.fileId,
      }));

      if (!isAuthenticated && anonymousEditToken)
        payload.anonymousEditToken = anonymousEditToken;

      if (!itemNo) {
        if (profile?.userId) {
          payload.userId = profile.userId;
        } else {
          payload.writerName = vals.writerName;
          payload.writerPassword = vals.writerPassword;
        }
      } else {
        if (data?.writer?.userId) payload.userId = data.writer.userId;
        else if (data?.writer?.name) payload.writerName = data.writer.name;
        else if (data?.writer?.email) payload.writerEmail = data.writer.email;
        if (!profile?.userId) payload.writerPassword = vals.writerPassword;
      }

      let embeddedFiles = [];

      if (vals?.embeddedFiles?.length) {
        const content = document.querySelector("#body .ql-editor");
        if (content) {
          const clonedContent = content.cloneNode(true);
          for await (const embeddedFile of vals.embeddedFiles) {
            const file = await doUploadSingleFile(
              embeddedFile,
              getFileUploadUrl,
              {
                boardCode: board?.boardCode,
              }
            );

            const imgId = embeddedFile.fileId;
            const img = clonedContent.querySelector(`img#${CSS.escape(imgId)}`);

            if (img) {
              img.setAttribute("src", file.fileId);
              img.setAttribute("id", file.fileId);
            }

            embeddedFiles.push({
              fileName: file.name,
              fileId: file.fileId,
            });
          }
          payload.body = clonedContent.innerHTML;
          payload.embeddedFiles = embeddedFiles;
        }
      }

      if (!itemNo) {
        createBoardItemMutation.mutate(
          { boardCode: board?.boardCode, payload },
          {
            onSuccess: (res) => {
              if (res) {
                methods.reset();
                alert("게시글 작성이 완료되었습니다.");
                queryClient.invalidateQueries({
                  queryKey: ["/board-item?boardCode={boardCode}"],
                });
                navigate(`/Family/Curious`);
              } else {
                alert("게시물 작성 또는 수정 중 오류가 발생했습니다.");
              }
            },
            onError: () => {
              alert("게시물 작성 또는 수정 중 오류가 발생했습니다.");
            },
            onSettled: () => {
              setLoadingScreen(false);
              if (showCaptcha) {
                methods.clearErrors("captcha");
                methods.setValue("captcha", "");
                methods.setValue("captchaToken", "");
                queryClient.invalidateQueries({
                  queryKey: ["/site-common/captcha/generate"],
                });
              }
            },
          }
        );
      } else {
        updateBoardItemMutation.mutate(
          {
            boardCode: board?.boardCode,
            itemNo,
            payload,
            privateViewToken,
          },
          {
            onSuccess: (res) => {
              if (res) {
                alert("게시물이 성공적으로 업데이트되었습니다.");
                queryClient.invalidateQueries({
                  queryKey: ["/board-item/{boardCode}/{itemNo}"],
                });
                navigate(
                  `/Family/Curious/${itemNo}${
                    privateViewToken
                      ? `?privateViewToken=${privateViewToken}`
                      : ""
                  }`
                );
              } else {
                alert("게시물 작성 또는 수정 중 오류가 발생했습니다.");
              }
            },
            onError: () => {
              alert("게시물 작성 또는 수정 중 오류가 발생했습니다.");
            },
            onSettled: () => {
              setLoadingScreen(false);
              if (showCaptcha) {
                methods.clearErrors("captcha");
                methods.setValue("captcha", "");
                methods.setValue("captchaToken", "");
                queryClient.invalidateQueries({
                  queryKey: ["/site-common/captcha/generate"],
                });
              }
            },
          }
        );
      }
    } else {
      alert(getPermissionMessage(BOARD_PERMISSION.WRITE));
    }
  };

  useEffect(() => {
    methods.setValue("isRequiredCaptcha", showCaptcha);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showCaptcha]);

  useEffect(() => {
    methods.setValue("isAuthenticated", isAuthenticated);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated]);

  useEffect(() => {
    if (!isLoading && data) {
      methods.setValue("email", data.writer?.email);
      methods.setValue("title", data.title);
      methods.setValue("body", renderBody(data.body, data.embeddedFiles));
      methods.setValue("writerName", data.writer?.name);
      methods.setValue("isPrivate", data.isPrivate);
      methods.setValue("privacyAccept", data.additionalItems?.privacyAccept);
      methods.setValue("videoUrl", data.additionalItems?.videoUrl);

      if (data.attachedFiles?.length) {
        const newFiles = data.attachedFiles.map((file) => ({
          fileId: file.fileId,
          name: file.fileName,
          file,
        }));

        methods.setValue("files", newFiles);
      }

      if (data.embeddedFiles?.length) {
        const newFiles = data.embeddedFiles.map((file) => ({
          fileId: file.fileId,
          name: file.fileName,
          file: {
            ...file,
            type: file.contentType,
          },
        }));
        methods.setValue("embeddedFiles", newFiles);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading, data]);

  useEffect(() => {
    if (profile?.email && !data?.writer?.email) {
      methods.setValue("email", profile.email);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profile]);

  useEffect(() => {
    if (
      !isBoardLoading &&
      !!board &&
      !checkRoleAndPermissions(
        roleCode,
        board,
        BOARD_PERMISSION.WRITE,
        nonPermissions,
        isAdmin
      )
    )
      alert(getPermissionMessage(BOARD_PERMISSION.WRITE));

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [board, isBoardLoading, roleCode, isAdmin]);

  return (
    <CheckRoleAndPermissionDisplay
      showMessage={!isBoardLoading && !!board}
      board={board}
      permission={BOARD_PERMISSION.WRITE}
      boardCode={boardCode}
    >
      <div className="border-t-2 border-primary md:pt-10 pt-6">
        <h2 className="font-semibold lg:text-3xl md:text-2xl text-xl mb-4">
          궁금합니다 작성
        </h2>

        <FormProvider {...methods}>
          <form onSubmit={methods.handleSubmit(onSubmit)}>
            <div className="flex flex-wrap mb-5">
              <label
                htmlFor="email"
                className="md:mt-4 md:w-[150px] w-full font-semibold"
              >
                이메일 <span className="text-error">*</span>
              </label>
              <div className="flex-1">
                <MyTextField
                  id="email"
                  name="email"
                  placeholder="이메일을 입력해주세요."
                />
              </div>
            </div>

            <div className="flex flex-wrap md:mb-5 mb-3">
              <label
                htmlFor="title"
                className="md:mt-4 md:w-[150px] w-full font-semibold"
              >
                질문 제목 <span className="text-error">*</span>
              </label>
              <div className="flex-1">
                <MyTextField
                  id="title"
                  name="title"
                  placeholder="제목을 입력해주세요."
                />
              </div>
            </div>

            {!isAuthenticated && (
              <div className="flex flex-wrap gap-x-5 gap-y-3 md:mb-5 mb-3">
                <div className="flex flex-wrap flex-1 md:min-w-[450px]">
                  <label
                    htmlFor="writerName"
                    className="md:mt-4 md:w-[150px] w-full font-semibold"
                  >
                    작성자 <span className="text-error">*</span>
                  </label>
                  <div className="flex-1">
                    <MyTextField
                      id="writerName"
                      name="writerName"
                      placeholder="작성자 이름을 입력해주세요."
                    />
                  </div>
                </div>
                <div className="flex flex-wrap flex-1 md:min-w-[450px]">
                  <label
                    htmlFor="writerPassword"
                    className="md:mt-4 md:w-[150px] w-full font-semibold"
                  >
                    비밀번호 <span className="text-error">*</span>
                  </label>
                  <div className="flex-1">
                    <MyTextField
                      id="writerPassword"
                      type="password"
                      name="writerPassword"
                      placeholder="비밀번호를 입력하세요."
                    />
                  </div>
                </div>
              </div>
            )}

            <div className="flex mb-5">
              <div className="md:mt-4 md:w-[150px] md:block hidden" />
              <div className="flex-1">
                <MyCheckboxCircle
                  name="isPrivate"
                  label="비밀글로 설정하기"
                  labelClassName="font-medium"
                />
              </div>
            </div>

            <div className="flex flex-wrap md:mb-5 mb-3">
              <label
                htmlFor="privacyAccept"
                className="md:mt-4 md:w-[150px] w-full font-semibold"
              >
                개인정보 처리방침 <span className="text-error">*</span>
              </label>
              <div className="flex-1">
                <div className="border border-gray-border overflow-y-auto small-scroll max-h-[200px] rounded md:p-5 p-3">
                  <Privacy />
                </div>

                <div className="flex justify-end mt-2 mb-6">
                  <MyCheckboxCircle
                    name="privacyAccept"
                    label="개인정보 수집 및 이용에 동의합니다."
                    required
                  />
                </div>
              </div>
            </div>

            <div className="flex flex-wrap md:mb-5 mb-3">
              <label
                htmlFor="body"
                className="md:mt-4 md:w-[150px] w-full font-semibold"
              >
                질문 내용 <span className="text-error">*</span>
              </label>
              <div className="flex-1">
                <MyEditor name="body" rows="8" cols="30" />
              </div>
            </div>

            <div className="flex flex-wrap md:mb-5 mb-3">
              <label
                htmlFor="files"
                className="md:mt-4 md:w-[150px] w-full font-semibold"
              >
                파일 첨부
              </label>
              <div className="flex-1">
                <MyInputFile
                  name="files"
                  multiple
                  placeholder="파일을 첨부해 주세요."
                  hint="*파일 첨부는 최대 5개까지 업로드 가능합니다."
                />
              </div>
            </div>

            <div className="flex flex-wrap md:mb-5 mb-3">
              <label
                htmlFor="videoUrl"
                className="md:mt-4 md:w-[150px] w-full font-semibold"
              >
                동영상
              </label>
              <div className="flex-1">
                <MyTextField
                  id="videoUrl"
                  name="videoUrl"
                  placeholder="동영상 URL을 입력해 주세요."
                />
              </div>
            </div>

            {showCaptcha && (
              <MyInputCaptcha
                name="captchaToken"
                className={isAuthenticated ? "hidden" : `md:mb-10 mb-5`}
                labelClass="md:w-[150px]"
              />
            )}

            <div className="flex items-center md:mt-10 mt-6 md:gap-4 gap-2">
              <MyButton
                variant="contained"
                color="#111111"
                className="font-semibold sm:min-w-[120px] mr-auto text-white"
                onClick={() =>
                  checkRoleAndPermissions(
                    roleCode,
                    board,
                    BOARD_PERMISSION.ACCESS,
                    nonPermissions,
                    isAdmin
                  )
                    ? navigate("/Family/Curious")
                    : alert(getPermissionMessage(BOARD_PERMISSION.ACCESS))
                }
              >
                목록보기
              </MyButton>
              <MyButton
                variant="outlined"
                className="sm:min-w-[120px]"
                onClick={() => {
                  if (itemNo) {
                    checkRoleAndPermissions(
                      roleCode,
                      board,
                      BOARD_PERMISSION.READ,
                      nonPermissions,
                      isAdmin
                    )
                      ? navigate(`/Family/Curious/${itemNo}`)
                      : alert(getPermissionMessage(BOARD_PERMISSION.READ));
                  } else {
                    checkRoleAndPermissions(
                      roleCode,
                      board,
                      BOARD_PERMISSION.ACCESS,
                      nonPermissions,
                      isAdmin
                    )
                      ? navigate("/Family/Curious")
                      : alert(getPermissionMessage(BOARD_PERMISSION.ACCESS));
                  }
                }}
              >
                <span className="font-semibold">취소</span>
              </MyButton>
              {checkShowSubmitButton && (
                <MyButton
                  variant="contained"
                  color="primary"
                  className="font-semibold sm:min-w-[120px]"
                  type="submit"
                >
                  저장
                </MyButton>
              )}
            </div>
          </form>
        </FormProvider>
      </div>
    </CheckRoleAndPermissionDisplay>
  );
};

export default FamilyUpsert06;
